/**
 * Build an inclusive slice from the ordered eligible event-date sequence.
 *
 * @param {PublicEventResource.PublicEventResource[]} eligibleDates
 * @param {PublicEventResource.PublicEventResource|undefined} start
 * @param {PublicEventResource.PublicEventResource|undefined} end
 * @return {PublicEventResource.PublicEventResource[]}
 */
function resolveContinuousDateRange(eligibleDates, start, end) {
    if (!start || !end) {
        return [];
    }

    const dates = sortEventDatesBySequence(eligibleDates);
    const startIndex = dates.findIndex(eventDate => eventDate.id === start.id);
    const endIndex = dates.findIndex(eventDate => eventDate.id === end.id);

    if (startIndex < 0 || endIndex < 0) {
        return [];
    }

    const firstIndex = Math.min(startIndex, endIndex);
    const lastIndex = Math.max(startIndex, endIndex);

    return dates.slice(firstIndex, lastIndex + 1);
}

/**
 * Resolve a direct click against the current constrained selection.
 *
 * @param {PublicEventResource.PublicEventResource[]} eligibleDates
 * @param {PublicEventResource.PublicEventResource[]} selectedDates
 * @param {PublicEventResource.PublicEventResource} clickedDate
 * @return {{dates: PublicEventResource.PublicEventResource[], valid: boolean}}
 */
function resolveContinuousDateRangeClick(eligibleDates, selectedDates, clickedDate) {
    const dates = sortEventDatesBySequence(eligibleDates);
    const selected = sortEventDatesBySequence(selectedDates);
    const clickedIndex = dates.findIndex(eventDate => eventDate.id === clickedDate?.id);

    if (clickedIndex < 0) {
        return {dates: selected, valid: false};
    }

    if (selected.length === 0) {
        return {dates: [dates[clickedIndex]], valid: true};
    }

    if (selected.length === 1) {
        if (selected[0].id === clickedDate.id) {
            return {dates: [], valid: true};
        }

        return {
            dates: resolveContinuousDateRange(dates, selected[0], dates[clickedIndex]),
            valid: true,
        };
    }

    const start = selected[0];
    const end = selected[selected.length - 1];
    const startIndex = dates.findIndex(eventDate => eventDate.id === start.id);
    const endIndex = dates.findIndex(eventDate => eventDate.id === end.id);

    if (startIndex < 0 || endIndex < 0) {
        return {dates: selected, valid: false};
    }

    if (clickedDate.id === start.id) {
        return {dates: [end], valid: true};
    }

    if (clickedDate.id === end.id) {
        return {dates: [start], valid: true};
    }

    const distanceFromStart = Math.abs(clickedIndex - startIndex);
    const distanceFromEnd = Math.abs(clickedIndex - endIndex);
    const nextStart = distanceFromStart < distanceFromEnd ? dates[clickedIndex] : start;
    const nextEnd = distanceFromStart < distanceFromEnd ? end : dates[clickedIndex];

    return {
        dates: resolveContinuousDateRange(dates, nextStart, nextEnd),
        valid: true,
    };
}

/**
 * Format the selected constrained endpoints for customer or admin presentation.
 *
 * @param {PublicEventResource.PublicEventResource[]} eligibleDates
 * @param {PublicEventResource.PublicEventResource[]} selectedDates
 * @param {boolean} operational
 * @return {string}
 */
function getContinuousDateRangeSummary(eligibleDates, selectedDates, operational = false) {
    const dates = sortEventDatesBySequence(selectedDates);

    if (dates.length === 0) {
        return '';
    }

    const formatDate = eventDate => {
        const baseLabel = operational
            ? (eventDate.operationalLongDateAndTime || eventDate.operationalDateAndTime)
            : eventDate.longDateAndTime;
        const fallbackLabel = eventDate.dateAndTime || eventDate.longDateOnly || eventDate.dateOnly || '';
        const matchingDates = sortEventDatesBySequence(eligibleDates).filter(candidate => {
            return candidate.eventDateTimestamp === eventDate.eventDateTimestamp;
        });

        if (matchingDates.length <= 1) {
            return baseLabel || fallbackLabel;
        }

        const duplicateIndex = matchingDates.findIndex(candidate => candidate.id === eventDate.id) + 1;

        return `${baseLabel || fallbackLabel} - ${eventDate.title || 'Performance'} ${duplicateIndex}`;
    };

    const startLabel = formatDate(dates[0]);

    if (dates.length === 1) {
        return startLabel;
    }

    return `${startLabel} - ${formatDate(dates[dates.length - 1])}`;
}

/**
 * @param {PublicEventResource.PublicEventResource[]} eventDates
 * @return {PublicEventResource.PublicEventResource[]}
 */
function sortEventDatesBySequence(eventDates) {
    const uniqueDatesById = new Map();

    (Array.isArray(eventDates) ? eventDates : []).forEach(eventDate => {
        if (eventDate?.id && !uniqueDatesById.has(eventDate.id)) {
            uniqueDatesById.set(eventDate.id, eventDate);
        }
    });

    return Array.from(uniqueDatesById.values()).sort((firstDate, secondDate) => {
        const firstTimestamp = Number(firstDate.eventDateTimestamp);
        const secondTimestamp = Number(secondDate.eventDateTimestamp);
        const normalizedFirstTimestamp = Number.isFinite(firstTimestamp) ? firstTimestamp : Number.MAX_SAFE_INTEGER;
        const normalizedSecondTimestamp = Number.isFinite(secondTimestamp) ? secondTimestamp : Number.MAX_SAFE_INTEGER;

        if (normalizedFirstTimestamp !== normalizedSecondTimestamp) {
            return normalizedFirstTimestamp - normalizedSecondTimestamp;
        }

        return String(firstDate.id).localeCompare(String(secondDate.id));
    });
}

/**
 * Project the live selected sequence into the dates that will be purchased.
 *
 * @param {PublicEventResource.PublicEventResource[]} selectedDates
 * @param {string} dateRangeMode
 * @return {PublicEventResource.PublicEventResource[]}
 */
function projectPurchasedDates(selectedDates, dateRangeMode = 'inclusive') {
    const dates = sortEventDatesBySequence(selectedDates);

    if (dateRangeMode !== 'checkout_exclusive' || dates.length < 2) {
        return dates;
    }

    return dates.slice(0, -1);
}

export {
    getContinuousDateRangeSummary,
    projectPurchasedDates,
    resolveContinuousDateRange,
    resolveContinuousDateRangeClick,
    sortEventDatesBySequence,
};
